import { useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { hasTransportParams, TransportAgreementDocument } from "mt-components";
import { gql } from "../../__generated__/gql";

type Props = {
  bookingId: string | null;
  onClose: () => void;
};

export default function TransportServiceAgreementModal({
  bookingId,
  onClose,
}: Props) {
  return (
    <Dialog
      open={bookingId !== null}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ "& .MuiDialog-paper": { maxWidth: 1200 } }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        Transport Service Agreement
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {bookingId && <ModalContent bookingId={bookingId} />}
      </DialogContent>
    </Dialog>
  );
}

function ModalContent({ bookingId }: { bookingId: string }) {
  const { data, loading, error } = useQuery(GET_BOOKING_FOR_TSA, {
    variables: { id: bookingId },
  });

  if (loading) return <CircularProgress />;
  if (error || !data || !hasTransportParams(data.booking))
    return <>Could not load agreement.</>;

  return <TransportAgreementDocument booking={data.booking} />;
}

const GET_BOOKING_FOR_TSA = gql(`
  query GetBookingForTsa($id: ID!) {
    booking(id: $id) {
      created
      currency
      amount
      payments {
        state
        amount
      }
      details {
        quantity
      }
      pickupLocation {
        name
        address
        query {
          placeId
          geoLocation {
            lat
            lng
          }
        }
      }
      group {
        time
        date
        customGroupItinerary {
          id
          stepDescription
          stepOrder
        }
        product {
          isCustomTour
          minimumInGroup
          meetingPlaceGoogleMapsUrl
          translation(lang: en) {
            name
            itinerary
            meetingPlaceName
            meetingPlaceDescription
          }
        }
      }
    }
  }
`);
